import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, Flex } from '@chakra-ui/react';

import { Table, TableFilter } from '@app/components/common/table';
import { useAlert, useApi } from '@app/hooks';
import { TxOutletItem, ScanningItem, Subordinate, TxLevelItem, TxReport, TxReportTotal } from '@app/api/models';
import { API_FEAT_DATE_FORMAT } from '@app/utils';
import { DateRangeField, Paper, Txt } from '@app/components/common';
import { DateRange } from '@app/components/common/date-range-picker/types';
import Dialog from '@app/components/common/modals/dialog';
import { Level } from '@app/api/models/structure';
import { TableBase } from '@app/components/common/table/table-base';

import { useColumns } from './columns';
import { txColumns } from './scanning-columns';
import { EquipmentTransactionsFilter, TxNodeItem } from './types';

export const EquipmentsTransactionsTable = () => {
  const api = useApi();
  const { success } = useAlert();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState<DateRange>({
    startDate: moment().subtract(1, 'day'),
    endDate: moment()
  });
  const [data, setData] = useState<TxReport>({
    total: {
      installations: 0,
      removals: 0,
    },
    report_data: {},
  });
  const [levels, setLevels] = useState<Level[]>([]);
  const [chain, setChain] = useState<TxNodeItem[]>([]);
  const [total, setTotal] = useState<TxReportTotal>({installations: 0, removals: 0});
  const [filter, setFilter] = useState<TableFilter<EquipmentTransactionsFilter>[]>([]);

  const [showDetail, setShowDetail] = useState<Subordinate<TxReportTotal, TxOutletItem>>();

  const fetchData = useCallback(async ({ period, filter }: {
    period: DateRange,
    filter: TableFilter<EquipmentTransactionsFilter>[]
  }) => { 
    setLoading(true);
    const { data: levelsData } = await api.structure.getLevels({
      filter: {
        is_active: 'true',
      },
      sort: '-value',
      limit: 10,
      offset: 0,
    });

    setLevels([...levelsData.data, 
      {
        id: -1,
        name: 'Outlets',
        value: -1,
        is_active: true,
      }
    ]);
    setChain([]);

    // const mapIds = (filterValue?: EntityBase[]) => {
    //   if (!filterValue || !filterValue.length) {return undefined;}
    //   return filterValue.map(({ id }) => Number(id));
    // };

    // const model_ids = mapIds(filter.find(({ name }) => name === 'model')?.value as EntityBase[]);
    // const brand_ids = mapIds(filter.find(({ name }) => name === 'brand')?.value as EntityBase[]);
    // const manufacturer_ids = mapIds(filter.find(({ name }) => name === 'manufacturer')?.value as EntityBase[]);

    const { data } = await api.reports.getTxReport({ 
      filter: {
        date_b: period.startDate.format(API_FEAT_DATE_FORMAT),
        date_e: period.endDate.format(API_FEAT_DATE_FORMAT),
      },
      download: false
    });
    setData(data);
    setTotal(data?.total || { installations: 0, removals: 0 });
    setLoading(false);
  }, []);

  const tableData = useMemo(() => {
    if (!data) {return [];}
    if (chain.length === 0) {
      return [...Object.keys(data.report_data).map<TxNodeItem>((key) => {
        return {
          id: key,
          title: data.report_data[key].employee?.name || 'Вакансия',
          subtitle: data.report_data[key].territory,
          level: data.report_data[key].level,
          total: data.report_data[key].total,
        };
      }),
        {
          id: '-1',
          title: 'Итого',
          subtitle: '',
          level: levels[0],
          total: data.total,
        }
      ];
    }

    let currentNode: TxLevelItem = data.report_data[chain[0].id];

    for (const item of chain.slice(1)) {
      currentNode = currentNode.subordinates[item.id];
    }
    
    return [
      ...chain, 
      ...Object.keys(currentNode.subordinates ||  {}).map<TxNodeItem>((key) => ({
        id: key,
        title: key == currentNode.subordinates[key].level.name ? '' : currentNode.subordinates[key].employee?.name || 'Вакансия',
        subtitle: key == currentNode.subordinates[key].level.name ? '[пусто]' :  currentNode.subordinates[key].territory,
        level: currentNode.subordinates[key].level,
        total: currentNode.subordinates[key].total,
      })),
      ...Object.keys(currentNode.outlets || {}).map<TxNodeItem>((key) => {
        return {
          id: key,
          title:  currentNode.outlets[key]?.outlet_name ? `[${key}] ${currentNode.outlets[key]?.outlet_name}` : 'ТТ неизвестно',
          subtitle: currentNode.outlets[key]?.outlet_address,
          level: levels[levels.length - 1],
          total: {
            installations: currentNode.outlets[key]?.installations || 0,
            removals: currentNode.outlets[key]?.removals || 0,
          },
        };
      }),
      {
        id: '-1',
        title: 'Итого',
        subtitle: '',
        level: levels[0],
        total: data.total,
      }
    ]; 
  }, [data, chain, levels]);

  const onItemClick = useCallback((item: TxNodeItem) => {
    setChain([...chain, item]);
  }, [chain]);

  const onMoveUpClick = useCallback((item: TxNodeItem) => {
    const index = chain.findIndex(({ id }) => id === item.id);
    setChain(chain.slice(0, index));
  }, [chain]);

  const onShowDetail = useCallback((item: TxNodeItem) => {
   //setShowDetail(item);
  }, []);

  const columns = useColumns({ chain, levels, onItemClick, onMoveUpClick, onShowDetail });


  const onChangePeriod = useCallback((period: DateRange) => {
    setPeriod(period);
    fetchData({period, filter});
  }, [fetchData, filter]);

  const download = useCallback(async () => {
    setLoading(true);
    const { code, message, data } = await api.reports.getTxReport({ 
      filter: {
        date_b: period.startDate.format(API_FEAT_DATE_FORMAT),
        date_e: period.endDate.format(API_FEAT_DATE_FORMAT),
      },
      download: true
    });
    if (code === 200) {
      success(message);
    }
    setLoading(false);
  }, [period, filter]); 

  const scanningData = useMemo(() => {
    
  }, [data, chain, showDetail]);

  return <>
    <Box mb={2}>
      <Paper>
        <Box px={6} py={4} display="flex">
          <Box mr={2} minWidth={400}>
            <DateRangeField
              id='period'
              label="Период"
              value={period}
              onChange={onChangePeriod}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
    <TableBase<TxNodeItem> 
      title="Отчет по установкам и демонтажам ХО" 
      columns={columns}
      data={tableData}
      loading={loading}
      onDownload={download}

      // onFilterChange={onFilterChange}
      // footerAdditional={<Box display="flex" flex={1} justifyContent="flex-end" alignItems='flex-end' gap={4}>
      //   <Txt>Итого:</Txt>
      //   <Flex gap={4}>
      //     <Flex gap={2} direction='column'>
      //       <Txt fontWeight='bold' >{total.installations}</Txt>
      //       <Txt color='gray.500'>установок</Txt>
      //     </Flex>
      //     <Flex gap={2} direction='column'>
      //       <Txt fontWeight='bold' >{total.removals}</Txt>
      //       <Txt color='gray.500'>демонтажей</Txt>
      //     </Flex>
      //   </Flex>
      // </Box>}
    />
    <Dialog open={!!showDetail}
      onClose={() => setShowDetail(undefined)}
      title="Отчет по установкам и демонтажам ХО"
      width={800}
    >
      <Box margin={-4}>
        <Table<ScanningItem>
          title=""
          columns={txColumns}
          data={[]}
          paper={false}
        />
      </Box>
    </Dialog>
  </>;
};